/**
 * TypeScript mirror of the API contracts.
 *
 * Source of truth:
 *   src/iERP.QboGateway.Api/Contracts/ApiContracts.cs
 *   src/iERP.QboGateway.Api/Contracts/AiInvoiceContract.cs
 *
 * Conventions:
 *   - JSON is camelCase (System.Text.Json default policy for minimal APIs / MVC).
 *   - Enums cross the wire as NUMBERS (default System.Text.Json enum handling). See ./enums.ts.
 *   - C# `decimal` -> number, `Guid` -> string, `long` -> number.
 *   - C# `DateTime` -> ISO-8601 string, C# `DateOnly` -> "yyyy-MM-dd" string.
 *   - C# nullable members are modelled as `| null` (the wire may also omit them entirely).
 */

import type {
  ApprovalReason,
  ApprovalStatus,
  DocumentStatus,
  GatewayRole,
  GatewayScenario,
  IntakeChannel,
  IntakeLineReviewState,
  IntakeLineType,
  LineRecommendationStatus,
  LineTaxResolution,
  PaymentScheduleSource,
  PayProfileConfidence,
  QboConnectionStatus,
  QboEntityType,
  RuleAction,
  ScheduledPaymentStatus,
  RuleField,
  RuleOperator,
  SyncDirection,
  VendorAgentStage,
  SyncOutcome,
  TaxReviewStatus,
  TenantFeatureAccess,
  TenantProvisioningStatus,
  VendorInvoiceIntakeStage,
} from './enums';

// ---------------------------------------------------------------------------
// Shared
// ---------------------------------------------------------------------------

/** Standard paged envelope returned by every list endpoint. */
export interface PagedResult<T> {
  items: T[];
  page: number;
  pageSize: number;
  totalCount: number;
  /** Computed server-side property; serialized because it is a public getter. */
  totalPages: number;
}

/** Uniform error body so the SPA can render failures consistently. */
export interface ApiErrorBody {
  code: string;
  message: string;
  detail?: string | null;
}

// ---------------------------------------------------------------------------
// Authentication and users
// ---------------------------------------------------------------------------

export interface SignInRequest {
  email: string;
  password: string;
}

export interface SignInResponse {
  accessToken: string;
  expiresUtc: string;
  user: CurrentUserDto;
}

export interface CurrentUserDto {
  id: string;
  tenantId: string;
  tenantName: string;
  email: string;
  displayName: string;
  role: GatewayRole;
  approvalLimit: number | null;
  permissions: string[];
}

export interface CreateUserRequest {
  email: string;
  displayName: string;
  role: GatewayRole;
  password: string | null;
  approvalLimit: number | null;
}

export interface UpdateUserRequest {
  displayName: string | null;
  role: GatewayRole | null;
  approvalLimit: number | null;
  isActive: boolean | null;
}

export interface UserDto {
  id: string;
  email: string;
  displayName: string;
  role: GatewayRole;
  approvalLimit: number | null;
  isActive: boolean;
  lastSignInUtc: string | null;
}

// ---------------------------------------------------------------------------
// QuickBooks connection
// ---------------------------------------------------------------------------

export interface QboConnectionDto {
  isConnected: boolean;
  status: QboConnectionStatus;
  realmId: string | null;
  companyName: string | null;
  countryCode: string | null;
  homeCurrencyCode: string | null;
  isSandbox: boolean;
  connectedUtc: string | null;
  accessTokenExpiresUtc: string | null;
  refreshTokenExpiresUtc: string | null;
  daysUntilReconsentRequired: number | null;
  lastSuccessfulCallUtc: string | null;
  lastError: string | null;
  connectedByEmail: string | null;
}

/** Returned by the connect endpoint; the SPA sends the user to `authorizationUrl`. */
export interface QboConnectStartResponse {
  authorizationUrl: string;
  state: string;
  expiresUtc: string;
}

/** Body for POST /api/qbo/connect. Not declared in ApiContracts.cs; the route documents `{returnUrl}`. */
export interface QboConnectRequest {
  returnUrl: string;
}

/** One account from the connected QuickBooks company's connector-scoped Chart of Accounts. */
export interface LocalAccountDto {
  id: string;
  externalAccountId: string;
  accountNumber: string | null;
  name: string;
  fullyQualifiedName: string | null;
  accountType: string | null;
  accountSubType: string | null;
  classification: string | null;
  currencyCode: string | null;
  isSubAccount: boolean;
  parentExternalAccountId: string | null;
  active: boolean;
  missingSinceUtc: string | null;
  isEligibleForNewPosting: boolean;
  firstSeenUtc: string;
  lastSeenUtc: string;
}

/** One item from the connected QuickBooks company's connector-scoped item (Product/Service) master. */
export interface LocalItemDto {
  id: string;
  externalItemId: string;
  name: string;
  fullyQualifiedName: string | null;
  /** QuickBooks Item.Type: Service, Inventory, NonInventory, or Category. */
  type: string | null;
  /** Item.PurchaseDesc — the description QuickBooks shows on a purchase line. */
  description: string | null;
  active: boolean;
  missingSinceUtc: string | null;
  isEligibleForNewPosting: boolean;
  firstSeenUtc: string;
  lastSeenUtc: string;
}

/** Purchase-only by design: Type is restricted to Service or NonInventory server-side, and
 * expenseAccountId is required — a two-sided or Inventory item needs decisions (an income account,
 * quantity tracking) this one form has no business making on a reviewer's behalf. */
export interface CreateLocalItemRequest {
  name: string;
  type: 'Service' | 'NonInventory';
  expenseAccountId: string;
  description?: string | null;
}

export interface ItemPullResponse {
  success: boolean;
  complete: boolean;
  recordsPulled: number;
  recordsCreated: number;
  recordsUpdated: number;
  recordsDeactivated: number;
  recordsNoLongerReported: number;
  completedUtc: string;
  message: string | null;
  errorCode: string | null;
}

// ---------------------------------------------------------------------------
// Documents
// ---------------------------------------------------------------------------

/** Metadata that accompanies a drag-and-drop upload (sent as multipart form fields). */
export interface UploadDocumentRequest {
  scenario: GatewayScenario;
  targetQboEntityId: string | null;
  qboVendorId: string | null;
  notes: string | null;
}

export interface DocumentSummaryDto {
  id: string;
  fileName: string;
  fileSizeBytes: number;
  pageCount: number | null;
  scenario: GatewayScenario;
  status: DocumentStatus;
  statusMessage: string | null;
  intakeChannel: IntakeChannel;
  externalJobId: string | null;
  vendorName: string | null;
  invoiceNumber: string | null;
  invoiceDate: string | null;
  dueDate: string | null;
  total: number | null;
  currencyCode: string | null;
  confidence: number | null;
  requiresVendorCreation: boolean;
  hasValidationIssues: boolean;
  /** The vendor this document is actually targeting right now — set at intake (a handshake
   * connection's bound vendor, or its currency-vendor link) or by a saved human override. Prefer
   * this over a name-similarity suggestion, which knows nothing about currency. */
  qboVendorId: string | null;
  qboEntityId: string | null;
  qboEntityKind: QboEntityType | null;
  qboDocNumber: string | null;
  uploadedByEmail: string | null;
  createdUtc: string;
  postedUtc: string | null;
  hasPendingApproval: boolean;
  /**
   * Set while this document is a vendor intake that still owes an invoice: the scenario it
   * returns to once its supplier is approved. Null for a document that is what it says it is.
   */
  vendorIntakeResumeScenario: GatewayScenario | null;

  /** Who is reviewing this right now, when somebody is. Null when nothing is claimed. */
  reviewClaimedByEmail: string | null;

  /** When the live claim lapses. Null when there is no live claim. */
  reviewClaimExpiresUtc: string | null;

  /**
   * True when the live claim is YOURS. "You have this" and "somebody else has this" have to read
   * differently, and an address alone cannot say which once the same person is signed in twice.
   */
  reviewClaimedByMe: boolean;
}

/**
 * Everything the review screen needs for one document. `contract` is the EFFECTIVE contract (the
 * original with any human edits applied) — the one that gets posted.
 */
export interface DocumentDetailDto {
  summary: DocumentSummaryDto;
  contract: AiInvoiceContract | null;
  history: SyncHistoryDto[];
  approvals: ApprovalDto[];
  suggestedVendorMatch: QboVendorMatchDto | null;
  documentDownloadUrl: string | null;
  /** The extraction exactly as the AI produced it, before any human edit. */
  originalContract: AiInvoiceContract | null;
  /** Human corrections, held separately from the original so evidence is never destroyed. */
  overrides: DocumentOverrideRequest | null;
  /** Current accounting revision. An approval is bound to the revision it was raised against. */
  revision: number;
  /** True when a person has changed something accounting-significant. */
  hasHumanEdits: boolean;
}

/** Operator edits applied on top of the AI contract before posting. */
export interface DocumentOverrideRequest {
  vendorName: string | null;
  qboVendorId: string | null;
  invoiceNumber: string | null;
  invoiceDate: string | null;
  dueDate: string | null;
  total: number | null;
  currencyCode: string | null;
  memo: string | null;
  lines: DocumentLineOverride[] | null;
  /**
   * The rate a person confirmed for a foreign-currency invoice. QuickBooks requires this whenever
   * the currency differs from the company's home currency, and the gateway never fetches one and
   * applies it on its own — a rate posts only if it was typed in here.
   */
  exchangeRate?: number | null;
  /**
   * Vendor-detail corrections made during a VENDOR INTAKE. Optional everywhere else: an invoice
   * edit leaves them undefined and behaves exactly as before. These are what the QuickBooks vendor
   * is created from AND what is taught back to iERP AI on approval, so the two cannot disagree.
   */
  vendorEmail?: string | null;
  vendorPhone?: string | null;
  vendorTaxRegistrationNo?: string | null;
  vendorAddressLine1?: string | null;
  vendorAddressLine2?: string | null;
  vendorCity?: string | null;
  /** State, province or county. */
  vendorState?: string | null;
  vendorPostalCode?: string | null;
  vendorCountry?: string | null;
}

/** What an operator confirmed on a vendor intake, and which vendor it belongs to. */
export interface VendorIntakeApprovalRequest {
  overrides: DocumentOverrideRequest;
  /**
   * Set when the operator chose an existing QuickBooks vendor rather than creating one. Its
   * presence is what forbids creation, so "use this vendor" can never make a second one.
   */
  useExistingQboVendorId: string | null;
}

/** What iERP AI recorded from an approved vendor intake. */
export interface VendorIntakeLearningResult {
  attempted: boolean;
  success: boolean;
  message: string | null;
  templateId: string | null;
  isTrusted: boolean;
}

/**
 * The result of approving a vendor intake. Posting and learning are reported separately: the vendor
 * either exists in QuickBooks or it does not, whereas learning only improves the NEXT document.
 */
export interface VendorIntakeApprovalResponse {
  posting: PostDocumentResponse | null;
  learning: VendorIntakeLearningResult | null;
  /**
   * Present when this intake was a hand-off from an invoice: what happened when the document went
   * back to being that invoice.
   */
  resume?: VendorIntakeHandoffResponse | null;
}

/** How an approved change to an EXISTING QuickBooks vendor ended. */
export type VendorUpdateState = 0 | 1 | 2 | 3;

export const VendorUpdateStates = {
  NotAttempted: 0 as VendorUpdateState,
  Applied: 1 as VendorUpdateState,
  Failed: 2 as VendorUpdateState,
  OutcomeUnknown: 3 as VendorUpdateState,
};

/**
 * What actually reached QuickBooks when an approval asked to change an existing supplier. Reported
 * on its own rather than buried in warnings, because "the supplier now holds these values" and "the
 * supplier is unchanged" are different facts and the operator has to see which one happened.
 */
export interface VendorUpdateReport {
  state: VendorUpdateState;
  message: string;
  fields: string[];
}

/** What happened to the learning half of an approved vendor intake. */
export type VendorIntakeLearningDisposition = 0 | 1 | 2 | 3 | 4;

export const VendorIntakeLearningDispositions = {
  Pending: 0 as VendorIntakeLearningDisposition,
  Confirmed: 1 as VendorIntakeLearningDisposition,
  NotEntitled: 2 as VendorIntakeLearningDisposition,
  Failed: 3 as VendorIntakeLearningDisposition,
  OutcomeUnknown: 4 as VendorIntakeLearningDisposition,
};

/**
 * The durable evidence of one vendor-intake approval.
 *
 * Every field is what it was AT THE MOMENT OF APPROVAL. The document has almost certainly been
 * extracted again as an invoice since, replacing the contract the decision was made against, so
 * none of this is reconstructible from the document itself.
 */
export interface VendorIntakeApprovalRecordDto {
  documentId: string;
  qboVendorId: string | null;
  /** True when the approval created the supplier; false when it adopted an existing one. */
  vendorCreated: boolean;
  resumeScenario: GatewayScenario | null;
  externalJobId: string | null;
  /** What the document proposed, as the operator was shown it. */
  proposed: Record<string, string | null>;
  /** What QuickBooks held before. Empty for a newly created supplier. */
  currentBefore: Record<string, string | null>;
  /** The fields the operator chose to write, and the values they submitted. */
  applied: Record<string, string | null>;
  /** The supplier as QuickBooks held it afterwards. This is what was taught to iERP AI. */
  authoritative: Record<string, string | null>;
  approvedByEmail: string | null;
  approvedUtc: string;
  learningDisposition: VendorIntakeLearningDisposition;
  learningMessage: string | null;
  learningTemplateId: string | null;
  learningConsensusStatus: string | null;
  learningDecidedUtc: string | null;
}

/** The outcome of handing a bill to vendor intake, or of resuming it afterwards. */
export interface VendorIntakeHandoffResponse {
  ok: boolean;
  /** False when the document was already in the asked-for state. Still a success. */
  changed: boolean;
  message: string;
  document: DocumentSummaryDto | null;
}

/**
 * One supplier field, three ways: what QuickBooks holds NOW, what this document suggests, and
 * whether the operator chose to overwrite the first with the second. Nothing is pre-ticked.
 */
export interface VendorIntakeFieldComparison {
  /** The override property this row writes, e.g. "vendorCity". */
  field: string;
  label: string;
  currentValue: string | null;
  suggestedValue: string | null;
  differs: boolean;
  /** False when the field cannot be changed on this vendor at all, whatever is approved. */
  editable: boolean;
  note: string | null;
}

export interface VendorIntakeComparisonResponse {
  qboVendorId: string | null;
  qboVendorName: string | null;
  vendorExists: boolean;
  /** False when the live read did not answer; no comparison may be shown from stale data. */
  currentValuesAvailable: boolean;
  message: string | null;
  fields: VendorIntakeFieldComparison[];
}

export interface DocumentLineOverride {
  lineNo: number;
  description: string | null;
  quantity: number | null;
  unitPrice: number | null;
  lineAmount: number | null;
  qboAccountId: string | null;
  qboItemId: string | null;
  exclude: boolean;
  unitOfMeasure?: string | null;
}

/** Administrative resolution of a document whose posting outcome is unknown. */
export interface ResolveUnknownRequest {
  /** "confirm" (the write landed) or "release" (it did not). */
  action: 'confirm' | 'release';
  qboEntityId: string | null;
  comment: string | null;
}

export interface PostDocumentRequest {
  overrides: DocumentOverrideRequest | null;
  createVendorIfMissing: boolean;
  attachSourceDocument: boolean;
}

export interface PostDocumentResponse {
  success: boolean;
  qboEntityId: string | null;
  entityKind: QboEntityType | null;
  qboDocNumber: string | null;
  message: string | null;
  warnings: string[];
  /** TokenUnavailable, ReconnectRequired, RealmChanged, VendorUpdateFailed, OutcomeUnknown, ... */
  errorCode?: string | null;
  /** What reached QuickBooks for an existing supplier. Null when no vendor change was asked for. */
  vendorUpdate?: VendorUpdateReport | null;
}

export interface RejectDocumentRequest {
  reason: string;
}

export interface ReferForApprovalRequest {
  assignToEmail: string | null;
  comment: string | null;
}

// ---------------------------------------------------------------------------
// Vendors
// ---------------------------------------------------------------------------

export interface VendorDto {
  id: string;
  qboVendorId: string;
  displayName: string;
  companyName: string | null;
  email: string | null;
  phone: string | null;
  taxIdentifier: string | null;
  addressLine1: string | null;
  city: string | null;
  state: string | null;
  postalCode: string | null;
  country: string | null;
  termsName: string | null;
  currencyCode: string | null;
  balance: number | null;
  active: boolean;
  lastSyncedUtc: string;
}

export interface UpsertVendorRequest {
  qboVendorId: string | null;
  displayName: string;
  companyName: string | null;
  email: string | null;
  phone: string | null;
  taxIdentifier: string | null;
  addressLine1: string | null;
  addressLine2: string | null;
  city: string | null;
  state: string | null;
  postalCode: string | null;
  country: string | null;
  termsId: string | null;
  currencyCode: string | null;
}

/** Result of matching an extracted vendor against the cached QuickBooks vendor list. */
export interface QboVendorMatchDto {
  qboVendorId: string | null;
  displayName: string | null;
  confidence: number;
  matchMethod: string;
  evidence: string | null;
  alternatives: QboVendorCandidateDto[];
}

export interface QboVendorCandidateDto {
  qboVendorId: string;
  displayName: string;
  confidence: number;
  matchMethod: string;
}

// ---------------------------------------------------------------------------
// Transactions (bills, purchase orders, expenses, payments)
// ---------------------------------------------------------------------------

export interface QboTransactionDto {
  id: string;
  entityType: QboEntityType;
  qboId: string;
  docNumber: string | null;
  qboVendorId: string | null;
  vendorName: string | null;
  txnDate: string | null;
  dueDate: string | null;
  totalAmount: number | null;
  balance: number | null;
  currencyCode: string | null;
  poStatus: string | null;
  memo: string | null;
  linkedToGateway: boolean;
  sourceDocumentId: string | null;
  lastSyncedUtc: string;
}

/** Ask the gateway to re-read a slice of QuickBooks into the local cache. */
export interface PullRequest {
  entityType: QboEntityType;
  changedSinceUtc: string | null;
  maxRecords: number | null;
}

export interface PullResponse {
  entityType: QboEntityType;
  recordsPulled: number;
  recordsCreated: number;
  recordsUpdated: number;
  completedUtc: string;
  message: string | null;
}

/**
 * Link a document to an open purchase order. The gateway posts a bill and references the purchase
 * order through LinkedTxn; it does not compare lines, quantities, prices or items. Names mirror the
 * API contract, the UI calls it linking.
 */
export interface MatchPurchaseOrderRequest {
  documentId: string;
  purchaseOrderQboId: string;
}

/** One ranked purchase order candidate the document could be linked to. */
export interface PurchaseOrderMatchSuggestionDto {
  purchaseOrderQboId: string;
  docNumber: string | null;
  vendorName: string | null;
  totalAmount: number | null;
  txnDate: string | null;
  confidence: number;
  reason: string;
}

// ---------------------------------------------------------------------------
// Approvals
// ---------------------------------------------------------------------------

export interface ApprovalDto {
  id: string;
  documentId: string;
  fileName: string;
  status: ApprovalStatus;
  reason: ApprovalReason;
  reasonDetail: string | null;
  amount: number | null;
  currencyCode: string | null;
  vendorName: string | null;
  invoiceNumber: string | null;
  confidence: number | null;
  requestedByEmail: string | null;
  assignedToEmail: string | null;
  decidedByEmail: string | null;
  decidedUtc: string | null;
  decisionComment: string | null;
  createdUtc: string;
  currentUserCanDecide: boolean;
  blockedReason: string | null;
  /** Document revision this approval was raised against. */
  documentRevision: number;
  /** True when the document has moved on, so this approval no longer applies. */
  isStale: boolean;
}

export interface ApprovalDecisionRequest {
  comment: string | null;
}

export interface RequestInfoRequest {
  comment: string;
  assignToEmail: string | null;
}

// ---------------------------------------------------------------------------
// Sync rules
// ---------------------------------------------------------------------------

export interface SyncRuleDto {
  id: string;
  name: string;
  description: string | null;
  appliesToScenario: GatewayScenario | null;
  action: RuleAction;
  priority: number;
  isEnabled: boolean;
  assignApprovalTo: string | null;
  timesMatched: number;
  lastMatchedUtc: string | null;
  conditions: SyncRuleConditionDto[];
}

export interface SyncRuleConditionDto {
  id: string;
  field: RuleField;
  operator: RuleOperator;
  value: string | null;
  sequence: number;
}

export interface UpsertSyncRuleRequest {
  name: string;
  description: string | null;
  appliesToScenario: GatewayScenario | null;
  action: RuleAction;
  priority: number;
  isEnabled: boolean;
  assignApprovalTo: string | null;
  conditions: UpsertSyncRuleConditionRequest[];
}

export interface UpsertSyncRuleConditionRequest {
  field: RuleField;
  operator: RuleOperator;
  value: string | null;
}

/** Dry-run a rule set against a document without changing anything. */
export interface TestSyncRuleRequest {
  documentId: string;
}

export interface TestSyncRuleResponse {
  matchedRuleId: string | null;
  matchedRuleName: string | null;
  resolvedAction: RuleAction;
  explanation: string;
  trace: RuleEvaluationTraceDto[];
}

export interface RuleEvaluationTraceDto {
  ruleId: string;
  ruleName: string;
  matched: boolean;
  conditionResults: string[];
}

// ---------------------------------------------------------------------------
// AI settings
// ---------------------------------------------------------------------------

export interface LegacyTenantAccountDefaultsDto {
  expenseAccountId: string | null;
  expenseAccountName: string | null;
  apAccountId: string | null;
  apAccountName: string | null;
  paymentAccountId: string | null;
  paymentAccountName: string | null;
  paymentType: string;
}

export interface AiSettingsDto {
  autoApproveConfidenceThreshold: number;
  reviewRequiredConfidenceThreshold: number;
  vendorMatchConfidenceThreshold: number;
  matchVendorByName: boolean;
  matchVendorByTaxId: boolean;
  matchVendorByEmail: boolean;
  autoCreateMissingVendor: boolean;
  extractLineItems: boolean;
  lineItemConfidenceThreshold: number;
  consolidateSimilarLines: boolean;
  allowInvoiceLineIerpAiExceptions: boolean;
  blockDuplicateDocuments: boolean;
  defaultExpenseAccountId: string | null;
  defaultExpenseAccountName: string | null;
  defaultApAccountId: string | null;
  defaultApAccountName: string | null;
  defaultPaymentAccountId: string | null;
  defaultPaymentAccountName: string | null;
  /** QuickBooks accepts only "Cash", "Check" or "CreditCard" on a Purchase. */
  defaultExpensePaymentType: string;
  attachSourceDocumentToQbo: boolean;
  modifiedUtc: string | null;
  modifiedByEmail: string | null;
  connectorInstanceId: string | null;
  connectorRealmId: string | null;
  connectorCompanyName: string | null;
  accountDefaultsConfirmed: boolean;
  accountDefaultsConfirmedByEmail: string | null;
  accountDefaultsConfirmedUtc: string | null;
  legacyTenantDefaults: LegacyTenantAccountDefaultsDto | null;
}

export interface UpdateAiSettingsRequest {
  autoApproveConfidenceThreshold: number;
  reviewRequiredConfidenceThreshold: number;
  vendorMatchConfidenceThreshold: number;
  matchVendorByName: boolean;
  matchVendorByTaxId: boolean;
  matchVendorByEmail: boolean;
  autoCreateMissingVendor: boolean;
  extractLineItems: boolean;
  lineItemConfidenceThreshold: number;
  consolidateSimilarLines: boolean;
  allowInvoiceLineIerpAiExceptions: boolean;
  blockDuplicateDocuments: boolean;
  defaultExpenseAccountId: string | null;
  defaultExpenseAccountName: string | null;
  defaultApAccountId: string | null;
  defaultApAccountName: string | null;
  defaultPaymentAccountId: string | null;
  defaultPaymentAccountName: string | null;
  /** QuickBooks accepts only "Cash", "Check" or "CreditCard" on a Purchase. */
  defaultExpensePaymentType: string;
  attachSourceDocumentToQbo: boolean;
}

// ---------------------------------------------------------------------------
// History and dashboard
// ---------------------------------------------------------------------------

export interface SyncHistoryDto {
  id: number;
  documentId: string | null;
  fileName: string | null;
  direction: SyncDirection;
  entityType: QboEntityType;
  outcome: SyncOutcome;
  operation: string;
  qboId: string | null;
  message: string | null;
  errorCode: string | null;
  httpStatusCode: number | null;
  durationMs: number;
  performedByEmail: string | null;
  intuitRequestId: string | null;
  createdUtc: string;
}

export interface DashboardDto {
  connection: QboConnectionDto;
  counts: DashboardCountsDto;
  recentDocuments: DocumentSummaryDto[];
  recentActivity: SyncHistoryDto[];
  volume: DailyVolumeDto[];
  botHealth: BotHealthDto;
}

export interface DashboardCountsDto {
  pendingApprovals: number;
  awaitingReview: number;
  processingNow: number;
  postedToday: number;
  failedToday: number;
  postedThisMonth: number;
  postedValueThisMonth: number;
  averageConfidenceThisMonth: number | null;
  postSuccessRatePercent: number;
}

/** `date` is a C# DateOnly, serialized as "yyyy-MM-dd". */
export interface DailyVolumeDto {
  date: string;
  received: number;
  posted: number;
  failed: number;
}

export interface BotHealthDto {
  reachable: boolean;
  baseUrl: string;
  version: string | null;
  latencyMs: number | null;
  message: string | null;
  checkedUtc: string;
}

// ---------------------------------------------------------------------------
// Signup, verification and account recovery
// ---------------------------------------------------------------------------

export interface SignUpRequest {
  companyName: string;
  contactName: string;
  email: string;
  password: string;
  phoneNo: string | null;
  countryCode: string | null;
  acceptedTerms: boolean;
}

/**
 * Deliberately vague about whether the address was already registered, so the endpoint cannot be
 * used to enumerate accounts. The user is told to check their email either way.
 */
export interface SignUpResponse {
  accepted: boolean;
  message: string;
  emailSentTo: string;
  linkValidForHours: number;
}

export interface ResendVerificationRequest {
  email: string;
}

/**
 * Outcome of asking for a fresh iERP AI licence-verification email. A refusal (cooldown, already
 * verified, registration not staged yet) arrives as an ApiError with the reason instead.
 */
export interface ResendLicenceVerificationResponse {
  success: boolean;
  message: string;
}

export interface ActivateAccountRequest {
  token: string;
}

/**
 * Returned on a successful activation. The session token is included so the browser goes straight
 * to the dashboard rather than making the user type their password again.
 */
export interface ActivateAccountResponse {
  success: boolean;
  message: string;
  accessToken: string | null;
  expiresUtc: string | null;
  user: CurrentUserDto | null;
  onboarding: TenantOnboardingDto | null;
}

export interface ForgotPasswordRequest {
  email: string;
}

export interface ResetPasswordRequest {
  token: string;
  newPassword: string;
}

export interface ChangePasswordRequest {
  currentPassword: string;
  newPassword: string;
}

/** Password rules, exposed so the SPA can validate before a round trip. */
export interface PasswordPolicyDto {
  minimumLength: number;
  requireUpper: boolean;
  requireLower: boolean;
  requireDigit: boolean;
  requireNonAlphanumeric: boolean;
  description: string;
}

// ---------------------------------------------------------------------------
// Invitations
// ---------------------------------------------------------------------------

export interface CreateInvitationRequest {
  email: string;
  displayName: string | null;
  role: GatewayRole;
  approvalLimit: number | null;
}

export interface InvitationDto {
  id: string;
  email: string;
  displayName: string | null;
  role: GatewayRole;
  approvalLimit: number | null;
  createdUtc: string;
  expiresUtc: string;
  acceptedUtc: string | null;
  revokedUtc: string | null;
  invitedByEmail: string | null;
  /** Computed server-side: still open for acceptance (not accepted, revoked or expired). */
  isOpen: boolean;
}

/** What an invitee sees before they set a password. No tenant internals are exposed. */
export interface InvitationPreviewDto {
  valid: boolean;
  message: string;
  email: string | null;
  companyName: string | null;
  role: GatewayRole | null;
}

export interface AcceptInvitationRequest {
  token: string;
  displayName: string;
  password: string;
}

// ---------------------------------------------------------------------------
// Tenant onboarding and licence state
// ---------------------------------------------------------------------------

/**
 * Drives the first-run checklist and the trial banner. Every field is safe to show a signed-in user
 * of the tenant.
 */
export interface TenantOnboardingDto {
  tenantName: string;
  tenantKey: string;
  provisioningStatus: TenantProvisioningStatus;
  provisioningMessage: string | null;
  canSubmitDocuments: boolean;
  /** Server-computed feature access — the API gate's own evaluation, not a client-side copy. */
  featureAccess: TenantFeatureAccess;
  steps: OnboardingStepDto[];
  licence: LicenceDto;
}

export interface OnboardingStepDto {
  key: string;
  title: string;
  description: string;
  complete: boolean;
  blocked: boolean;
  actionRoute: string | null;
  actionLabel: string | null;
}

/** Licence and quota state, mirrored from the Registration Server and the bot. */
export interface LicenceDto {
  status: string | null;
  active: boolean;
  tier: string | null;
  packageCode: string | null;
  subscriptionStartDate: string | null;
  subscriptionEndDate: string | null;
  daysRemaining: number | null;
  message: string | null;
  monthlyLimit: number | null;
  remainingThisMonth: number | null;
  dailyLimit: number | null;
  remainingToday: number | null;
  checkedUtc: string | null;
  quotaExhausted: boolean;
  isTrial: boolean;
}

/** Current subscription block returned by the Registration Server through iERP AI. */
export interface LicenceCurrentSubscriptionDto {
  active?: boolean;
  packageCode?: string | null;
  description?: string | null;
  billingPeriod?: string | null;
  currencyCode?: string | null;
  aiPowerCode?: string | null;
  aiPowerDescription?: string | null;
  originalPackageAmount?: number | null;
  subscriptionStartDate?: string | null;
  subscriptionEndDate?: string | null;
  tierRank?: number | null;
  creditAvailable?: boolean;
  availableUpgradeCredit?: number | null;
}

/** One authoritative usage/feature limit published by the Registration Server. */
export interface LicencePackageLimitDto {
  moduleCode?: string | null;
  operationCode?: string | null;
  periodType?: string | null;
  limitQuantity?: number | null;
  overagePrice?: number | null;
}

/** One authoritative offer. Prices and capabilities are never seeded in the QBO Gateway. */
export interface LicencePackageOfferDto {
  moduleCode?: string | null;
  moduleName?: string | null;
  packageCode?: string | null;
  licenseProductCode?: string | null;
  description?: string | null;
  currencyCode?: string | null;
  billingPeriod?: string | null;
  baseUnitPrice?: number | null;
  aiPowerPriceDifference?: number | null;
  unitPrice?: number | null;
  aiPowerCode?: string | null;
  aiPowerDescription?: string | null;
  priceSource?: string | null;
  stripePriceId?: string | null;
  trialPackage?: boolean;
  defaultRegisteredPackage?: boolean;
  offerType?: string | null;
  featureCode?: string | null;
  compatibleBasePackages?: string | null;
  includedFromPackage?: string | null;
  prerequisiteFeatures?: string | null;
  grantedFeatures?: string | null;
  baseTierRank?: number | null;
  minimumBaseTierRank?: number | null;
  includedFromTierRank?: number | null;
  displayInAddOnPopup?: boolean;
  employeeAllowance?: number | null;
  employeeOveragePrice?: number | null;
  matrixCaption?: string | null;
  matrixSortOrder?: number | null;
  displayInPackageMatrix?: boolean;
  limits?: LicencePackageLimitDto[];
  sharedPages?: string | null;
  intakeAreas?: string | null;
  ocr?: string | null;
  recommendationCards?: string | null;
  aiLearning?: string | null;
  historicalLearning?: string | null;
  aiPredictedPosting?: string | null;
  emailMonitoring?: string | null;
  handshakes?: string | null;
  dataExchange?: string | null;
  keyDifferentiators?: string | null;
  processingOptionExplanation?: string | null;
  isCurrentPackage?: boolean;
  upgradeEligible?: boolean;
  upgradeCreditAmount?: number | null;
  netUpgradeBeforeTax?: number | null;
  upgradeBlockedReason?: string | null;
  displaySortOrder?: number | null;
}

export interface LicenceMatrixColumnDto {
  columnCode?: string | null;
  caption?: string | null;
  tooltip?: string | null;
  detailedExplanation?: string | null;
  sortOrder?: number | null;
}

export interface LicenceMatrixValueDto {
  columnCode?: string | null;
  value?: string | null;
}

export interface LicenceMatrixRowDto {
  packageCode?: string | null;
  sortOrder?: number | null;
  values?: LicenceMatrixValueDto[];
}

export interface LicenceActiveAddOnDto {
  packageCode?: string | null;
  featureCode?: string | null;
  description?: string | null;
  basePackageCode?: string | null;
  grantedFeatures?: string | null;
  employeeAllowance?: number | null;
  employeeOveragePrice?: number | null;
  subscriptionStartDate?: string | null;
  subscriptionEndDate?: string | null;
}

/** Product-scoped catalogue relayed from iERP AI after the API proves it is QUICKBOOKSONLINE. */
export interface LicencePackageCatalogDto {
  registrationId?: string | null;
  licenseProductCode?: string | null;
  moduleCode?: string | null;
  moduleName?: string | null;
  currencyCode?: string | null;
  billingPeriod?: string | null;
  aiPowerFilter?: string | null;
  currentSubscription?: LicenceCurrentSubscriptionDto | null;
  activeAddOns?: LicenceActiveAddOnDto[];
  offers?: LicencePackageOfferDto[];
  matrixColumns?: LicenceMatrixColumnDto[];
  matrixRows?: LicenceMatrixRowDto[];
}

export interface LicencePlanConfiguratorDto {
  registrationId?: string | null;
  licenseProductCode?: string | null;
  moduleCode?: string | null;
  moduleName?: string | null;
  currencyCode?: string | null;
  catalogVersion?: number | null;
  catalogPublishedUtc?: string | null;
  catalogSource?: string | null;
  currentSubscription?: LicenceCurrentSubscriptionDto | null;
  activeAddOns?: LicenceActiveAddOnDto[];
  matrixColumns?: LicenceMatrixColumnDto[];
  matrixRows?: LicenceMatrixRowDto[];
  baseOfferVariants?: LicencePackageOfferDto[];
  addOnVariants?: LicencePackageOfferDto[];
  recommendedAiPowerByPackage?: Record<string, string>;
}

export interface CreateLicencePurchaseRequest {
  packageCode: string;
  addOnPackageCodes: string[];
  currencyCode?: string | null;
  billingPeriod: 'MONTH' | 'YEAR';
  aiPowerFilter: string;
  /** Buy another term of the plan already active, bypassing the "nothing changed" refusal. */
  renewCurrentPlan?: boolean;
}

export interface LicencePurchaseLineDto {
  packageCode?: string | null;
  description?: string | null;
  offerType?: string | null;
  quantity?: number | null;
  unitPrice?: number | null;
  lineAmount?: number | null;
}

/** Authoritative order/tax/Stripe response returned by Registration Server through iERP AI. */
export interface LicencePurchaseResponseDto {
  licensePurchaseId?: string | null;
  registrationId?: string | null;
  licenseProductCode?: string | null;
  packageCode?: string | null;
  basePackageCode?: string | null;
  billingPeriod?: string | null;
  currencyCode?: string | null;
  subtotalBeforeTax?: number | null;
  taxAmount?: number | null;
  amountToPay?: number | null;
  amountIncludingTax?: number | null;
  status?: string | null;
  paymentStatus?: string | null;
  paymentMethod?: string | null;
  salesOrderNo?: string | null;
  salesInvoiceNo?: string | null;
  stripeEligible?: boolean;
  stripePaymentUrl?: string | null;
  stripeMode?: string | null;
  lastMessage?: string | null;
  lines?: LicencePurchaseLineDto[];
}

// ---------------------------------------------------------------------------
// AI invoice contract (mirrors Contracts/AiInvoiceContract.cs)
// ---------------------------------------------------------------------------

/** Adjudicated extraction result for one document. */
export interface AiInvoiceContract {
  externalJobId: string;
  tenantId: string;
  scenario: string;
  processingMode: string | null;
  extractionMode: string | null;
  processedUtc: string;
  suggestedAction: string;
  isReadyForImport: boolean;
  isReadyForVendorSetup: boolean;
  isReadyForPurchaseInvoiceImport: boolean;
  isReadyForDocumentMatch: boolean;
  requiresVendorCreation: boolean;
  requiresVendorValidation: boolean;
  requiresUserReview: boolean;
  vendorValidationStatus: string;
  vendorMatchesContext: boolean | null;
  vendor: AiVendorContract;
  header: AiInvoiceHeaderContract;
  amounts: AiInvoiceAmountsContract;
  lines: AiInvoiceLineContract[];
  taxes: AiInvoiceTaxContract[];
  warnings: AiWarningContract[];
  validationIssues: AiValidationIssueContract[];
  invoiceEmailAddresses: AiInvoiceEmailAddressContract[];
  readinessScore: AiReadinessScore;
  templateSignature: string | null;
  templateId: string | null;
  requiresPaymentPostingReview: boolean;
  companyInformationMatches: boolean;
  companyInformationWarning: string | null;
}

/**
 * Vendor detail lifted from the document. The `businessCentral*` wire names are the bot's own
 * format; the gateway reads them as generic host-ERP match hints.
 */
export interface AiVendorContract {
  name: string | null;
  email: string | null;
  phone: string | null;
  taxRegistrationNo: string | null;
  taxLiable: boolean;
  address: string | null;
  address2: string | null;
  city: string | null;
  county: string | null;
  postCode: string | null;
  countryRegionCode: string | null;
  bankName: string | null;
  bankAccountNo: string | null;
  bankRoutingNo: string | null;
  bankIban: string | null;
  bankSwiftBic: string | null;
  remitToName: string | null;
  remitToAddress: string | null;
  remitToCity: string | null;
  remitToPostCode: string | null;
  remitToCountryRegionCode: string | null;
  remitToEmail: string | null;
  /** Host-ERP (QuickBooks) vendor id the bot matched to. */
  businessCentralVendorNo: string | null;
  businessCentralVendorName: string | null;
  businessCentralVendorMatchMethod: string | null;
  businessCentralVendorMatchConfidence: number | null;
  businessCentralVendorMatchEvidence: string | null;
}

export interface AiInvoiceHeaderContract {
  invoiceNumber: string | null;
  invoiceDate: string | null;
  dueDate: string | null;
  currencyCode: string | null;
  paymentTermsText: string | null;
  purchaseOrderNo: string | null;
  buyerReference: string | null;
  taxAreaCode: string | null;
  taxLiable: boolean | null;
}

export interface AiInvoiceAmountsContract {
  subtotal: number | null;
  discountAmount: number | null;
  taxAmount: number | null;
  shippingAmount: number | null;
  invoiceTotal: number | null;
}

export interface AiInvoiceLineContract {
  lineNo: number;
  description: string | null;
  itemReference: string | null;
  quantity: number | null;
  unitOfMeasure: string | null;
  unitPrice: number | null;
  lineDiscountAmount: number | null;
  lineAmount: number | null;
  purchaseOrderLineReference: string | null;
  taxGroupCode: string | null;
  recommendationConfidence: number | null;
  recommendationReason: string | null;
  recommendationStatus: string | null;
  /**
   * iERP AI's advisory account suggestion for the line. Display and explicit acceptance only; the
   * posted account is always the reviewer's override. Contracts stored before this existed omit
   * these members entirely, so read them with `?? null`.
   */
  suggestedAccountId: string | null;
  suggestedAccountName: string | null;
  suggestedAccountConfidence: number | null;
  suggestedAccountReason: string | null;
}

export interface AiInvoiceTaxContract {
  taxCode: string | null;
  taxRate: number | null;
  taxAmount: number | null;
  taxableBaseAmount: number | null;
  confidence: number | null;
  source: string | null;
}

export interface AiWarningContract {
  code: string;
  message: string;
  severity: string;
}

export interface AiValidationIssueContract {
  fieldName: string;
  severity: string;
  message: string;
}

export interface AiInvoiceEmailAddressContract {
  emailAddress: string;
  domain: string;
  sourceHint: string;
  occurrenceCount: number;
}

/**
 * Bot readiness scoring. The gateway treats `overallScore` as the single extraction confidence
 * number surfaced in the UI and used by sync rules.
 */
export interface AiReadinessScore {
  overallScore: number;
  headerScore: number;
  vendorScore: number;
  lineScore: number;
  summary: string | null;
}

// ---------------------------------------------------------------------------
// Multi-user company access
// ---------------------------------------------------------------------------

/** The state of the review claim on one document. */
export interface ReviewClaimDto {
  documentId: string;
  claimedByEmail: string | null;
  claimedUtc: string | null;
  expiresUtc: string | null;
  claimedByMe: boolean;
  message: string;
}

/**
 * The company-level controls that exist because several people share one QuickBooks company.
 * Readable by anyone signed in - a rule that constrains you is a rule you are entitled to see -
 * and writable only by an administrator.
 */
export interface CompanyAccessPolicyDto {
  requireSeparateApprover: boolean;
  selfApprovalAllowedUpTo: number | null;
  requireReviewClaim: boolean;
  reviewClaimMinutes: number;
  modifiedUtc: string | null;
  modifiedByEmail: string | null;
  /** True when this is the built-in default rather than something somebody saved. */
  isDefault: boolean;
}

export interface UpdateCompanyAccessPolicyRequest {
  requireSeparateApprover: boolean;
  selfApprovalAllowedUpTo: number | null;
  requireReviewClaim: boolean;
  reviewClaimMinutes: number;
}

// ---------------------------------------------------------------------------
// Vendor invoice intake
// ---------------------------------------------------------------------------

export interface VendorInvoiceIntakeSummaryDto {
  documentId: string;
  stage: VendorInvoiceIntakeStage;
  /** The one-line reason this is where it is, in the words a person needs. */
  stageReason: string;
  fileName: string;
  channel: IntakeChannel;
  sourceDocumentType: string | null;
  vendorName: string | null;
  qboVendorId: string | null;
  invoiceNumber: string | null;
  invoiceDate: string | null;
  dueDate: string | null;
  total: number | null;
  currencyCode: string | null;
  confidence: number | null;
  lineCount: number;
  linesAwaitingReview: number;
  headerReviewed: boolean;
  taxReviewStatus: TaxReviewStatus;
  taxDifference: number | null;
  requiresVendorCreation: boolean;
  hasValidationIssues: boolean;
  hasLowConfidenceLines: boolean;
  lineReconciliationVariance: number | null;
  requiresApproval: boolean;
  hasPendingApproval: boolean;
  revision: number;
  qboEntityId: string | null;
  qboDocNumber: string | null;
  sourceDocumentAttached: boolean;
  uploadedByEmail: string | null;
  reviewClaimedByEmail: string | null;
  reviewClaimExpiresUtc: string | null;
  reviewClaimedByMe: boolean;
  createdUtc: string;
  postedUtc: string | null;
  /**
   * False when this document has no VendorInvoiceIntake projection yet, or reached a terminal
   * status before extraction ever produced one. Previous/Next should skip these rather than land
   * on a document that always answers "NoExtraction".
   */
  hasExtraction: boolean;
}

export interface VendorInvoiceIntakeLineDto {
  lineNo: number;
  lineType: IntakeLineType;
  description: string | null;
  quantity: number | null;
  unitCost: number | null;
  lineAmount: number | null;
  qboAccountId: string | null;
  qboAccountName: string | null;
  qboItemId: string | null;
  qboItemName: string | null;
  qboTaxCodeId: string | null;
  qboTaxCodeName: string | null;
  taxPercent: number | null;
  calculatedTaxAmount: number | null;
  /** The tax text printed on the invoice. Evidence for the reviewer, never sent to QuickBooks. */
  extractedTaxCodeText: string | null;
  /** What happened when this line's tax code was last resolved. */
  taxResolution: LineTaxResolution;
  excluded: boolean;
  confidence: number | null;
  lowConfidence: boolean;
  recommendationStatus: LineRecommendationStatus;
  recommendedAccountId: string | null;
  recommendedAccountName: string | null;
  recommendationConfidence: number | null;
  recommendationBasis: string | null;
  reviewState: IntakeLineReviewState;
  reviewedByEmail: string | null;
  reviewedUtc: string | null;
  reviewNote: string | null;
}

export interface IntakeTaxReviewDto {
  status: TaxReviewStatus;
  sourceTaxAmount: number | null;
  sourceTaxPercent: number | null;
  sourceTaxableBase: number | null;
  sourceTaxEvidence: string | null;
  sourceTaxConfidence: number | null;
  sourceTaxEvidenceSource: string | null;
  calculatedTaxAmount: number | null;
  taxDifference: number | null;
  tolerance: number;
  lastCalculatedUtc: string | null;
  reviewedByEmail: string | null;
  reviewedUtc: string | null;
  reviewNote: string | null;
  /**
   * True in a company where Intuit computes sales tax itself. The calculated figure is then a
   * cross-check rather than the number QuickBooks will use, and the screen says so.
   */
  automatedSalesTax: boolean;
  /** Why tax blocks a post right now, or null. */
  blocker: string | null;
  /** What the action you just performed did. Never the stored acceptance reason. */
  actionMessage: string | null;
}

export interface VendorInvoiceIntakeDetailDto {
  summary: VendorInvoiceIntakeSummaryDto;
  lines: VendorInvoiceIntakeLineDto[];
  tax: IntakeTaxReviewDto;
  extractedPaymentTermsText: string | null;
  qboTermsId: string | null;
  qboTermsName: string | null;
  paymentTermsMappingSkipped: boolean;
  paymentReviewRequired: boolean;
  paymentReviewReason: string | null;
  suggestedVendorName: string | null;
  manualVendorSelection: boolean;
  qboAttachableId: string | null;
  attachmentMessage: string | null;
  documentDownloadUrl: string | null;
  blockers: string[];
  approvals: ApprovalDto[];
  vendorIntakeResumeScenario: GatewayScenario | null;
}

/**
 * A decision about one line. The coding fields are the whole line as it should now read, not a
 * patch: a line override REPLACES the stored override for that line number, so sending only the tax
 * code would clear the account.
 */
export interface IntakeLineReviewRequest {
  decision: IntakeLineReviewState;
  description: string | null;
  quantity: number | null;
  unitCost: number | null;
  lineAmount: number | null;
  qboAccountId: string | null;
  qboItemId: string | null;
  qboTaxCodeId: string | null;
  reviewNote: string | null;
  /** The revision your screen was built from. Refused when it has moved on. */
  expectedRevision: number | null;
}

/** One line's decision within a batch save — same fields as IntakeLineReviewRequest, minus the
 * revision, which the batch names once for the whole set. */
export interface IntakeLineBatchEntry {
  lineNo: number;
  decision: IntakeLineReviewState;
  description: string | null;
  quantity: number | null;
  unitCost: number | null;
  lineAmount: number | null;
  qboAccountId: string | null;
  qboItemId: string | null;
  qboTaxCodeId: string | null;
  reviewNote: string | null;
}

/** Several lines' decisions, saved together as one edit — one revision bump instead of one per line. */
export interface IntakeLineBatchReviewRequest {
  lines: IntakeLineBatchEntry[];
  expectedRevision: number | null;
}

export interface IntakeHeaderReviewRequest {
  reviewed: boolean;
  qboTermsId: string | null;
  qboTermsName: string | null;
  paymentTermsMappingSkipped: boolean;
  /** The revision the screen was built from. Refused when it has moved on. */
  expectedRevision: number | null;
}

export interface AcceptTaxDifferenceRequest {
  note: string;
  /** The revision the acceptance was decided against. Refused when it has moved on. */
  expectedRevision: number | null;
}

export interface QboTaxCodeDto {
  id: string;
  name: string;
  purchaseRatePercent: number | null;
  taxable: boolean;
  automatedSalesTax: boolean;
}

export interface PullTaxCodesResponse {
  success: boolean;
  pulled: number;
  created: number;
  updated: number;
  deactivated: number;
  automatedSalesTax: boolean;
  message: string | null;
}

export interface QboTermDto {
  id: string;
  name: string;
}

export interface PullTermsResponse {
  success: boolean;
  pulled: number;
  created: number;
  updated: number;
  deactivated: number;
  message: string | null;
}

// ---------------------------------------------------------------------------
// Trading partners: the company directory and the connection lifecycle.
//
// Shapes mirror Contracts/PartnerContracts.cs and Contracts/IerpAiPartnerContracts.cs. The
// central store is the authority for connections; what this gateway holds is a mirror, which is
// why every list call refreshes before it reads and why LastSyncedUtc is worth showing.
// ---------------------------------------------------------------------------

/** CompanyProfileDraftStatus. Draft and Approved both disclose NOTHING; only Published does. */
export type ProfileDraftStatus = 'Draft' | 'Approved' | 'Published' | 'Superseded';

export interface ProfileDraftDto {
  id: string;
  version: number;
  status: string;
  companyName: string;
  tradeName: string | null;
  address: string | null;
  address2: string | null;
  city: string | null;
  county: string | null;
  postCode: string | null;
  countryRegionCode: string | null;
  contactName: string | null;
  contactEmail: string | null;
  phoneNo: string | null;
  registrationNo: string | null;
  vatRegistrationNo: string | null;
  taxRegistrationNo: string | null;
  businessEmailDomain: string | null;
  homePage: string | null;
  showInDirectory: boolean;
  createdByEmail: string | null;
  approvedByEmail: string | null;
  approvedUtc: string | null;
  publishedUtc: string | null;
  centralProfileId: string | null;
  publishMessage: string | null;
  createdUtc: string;

  // Per-field disclosure, mirroring Business Central's own "Dir. Setup iER" checkboxes: whether a
  // company that finds or connects to this profile actually sees each field, independent of
  // whether the field has a value at all. There is no such flag for taxRegistrationNo - the
  // central directory has no concept of hiding just that one field.
  showCompanyName: boolean;
  showTradeName: boolean;
  showAddress: boolean;
  showCity: boolean;
  showCounty: boolean;
  showPostCode: boolean;
  showCountryRegionCode: boolean;
  showBusinessEmailDomain: boolean;
  showContactName: boolean;
  showContactEmail: boolean;
  showPhoneNo: boolean;
  showRegistrationNo: boolean;
  showVatRegistrationNo: boolean;
  showHomePage: boolean;
}

/** Every field the draft editor may change. Omitted fields are left as they are. */
export interface ProfileDraftEdit {
  companyName?: string | null;
  tradeName?: string | null;
  address?: string | null;
  address2?: string | null;
  city?: string | null;
  county?: string | null;
  postCode?: string | null;
  countryRegionCode?: string | null;
  contactName?: string | null;
  contactEmail?: string | null;
  phoneNo?: string | null;
  registrationNo?: string | null;
  vatRegistrationNo?: string | null;
  taxRegistrationNo?: string | null;
  businessEmailDomain?: string | null;
  homePage?: string | null;
  showInDirectory?: boolean | null;

  showCompanyName?: boolean | null;
  showTradeName?: boolean | null;
  showAddress?: boolean | null;
  showCity?: boolean | null;
  showCounty?: boolean | null;
  showPostCode?: boolean | null;
  showCountryRegionCode?: boolean | null;
  showBusinessEmailDomain?: boolean | null;
  showContactName?: boolean | null;
  showContactEmail?: boolean | null;
  showPhoneNo?: boolean | null;
  showRegistrationNo?: boolean | null;
  showVatRegistrationNo?: boolean | null;
  showHomePage?: boolean | null;
}

export interface DirectoryEntryDto {
  profileId: string | null;
  registrationId: string | null;
  tenantId: string | null;
  companyId: string | null;
  companyName: string | null;
  tradeName: string | null;
  city: string | null;
  county: string | null;
  countryRegionCode: string | null;
  businessEmailDomain: string | null;
  address: string | null;
  address2: string | null;
  postCode: string | null;
  environmentType: string | null;
  contactName: string | null;
  contactEmail: string | null;
  phoneNo: string | null;
  registrationNo: string | null;
  vatRegistrationNo: string | null;
  anonymousDirectoryEntry: boolean;
  taxRegistrationMatched: boolean;
}

export interface DirectorySearchResponse {
  success: boolean;
  message: string | null;
  companies: DirectoryEntryDto[];
  totalCount: number;
  errors: string[];
}

export interface DirectorySearchRequest {
  searchText?: string | null;
  countryRegionCode?: string | null;
  county?: string | null;
  postCode?: string | null;
  skip?: number;
  take?: number;
}

/** MyVendor: they invoice us. MyCustomer: we invoice them. TradingPartner: both. */
export type RelationshipRole = 'MyVendor' | 'MyCustomer' | 'TradingPartner';

export interface PartnerConnectionDto {
  id: string;
  connectionId: string;
  direction: string;
  relationshipRole: string;
  isBuyer: boolean;
  status: string;
  counterpartyCompanyName: string | null;
  counterpartyCompanyId: string | null;
  counterpartyProfileId: string | null;
  boundQboVendorId: string | null;
  boundQboCustomerId: string | null;
  boundByEmail: string | null;
  boundUtc: string | null;
  /** Currency code -> QBO vendor id overrides of boundQboVendorId, for a partner that invoices in
   * more than one currency (QuickBooks locks a vendor's currency permanently, so a second currency
   * needs a second vendor card there). Empty when this connection has never needed one. */
  currencyVendorLinks: Record<string, string>;
  sellerInvoiceSendingEnabled: boolean;
  buyerInvoiceReceivingEnabled: boolean;
  capabilityChangedUtc: string | null;
  autoApplyCompanyInfoUpdates: boolean;
  hasPendingCompanyInfoUpdate: boolean;
  pendingUpdateStatus: string | null;
  pendingUpdateReceivedUtc: string | null;
  message: string | null;
  requestedUtc: string | null;
  respondedUtc: string | null;
  revokedUtc: string | null;
  lastSyncedUtc: string;
}

export interface RequestConnectionRequest {
  targetProfileId?: string | null;
  targetRegistrationId?: string | null;
  targetCompanyName?: string | null;
  relationshipRole: RelationshipRole;
  message?: string | null;
  localVendorId?: string | null;
  localCustomerId?: string | null;
  /** From the directory search result (DirectoryEntryDto.tenantId) — iERP AI refuses a connection
   * request with no target tenant id. */
  targetTenantId?: string | null;
  /** Same story as targetTenantId — from DirectoryEntryDto.companyId. */
  targetCompanyId?: string | null;
  /** Same story again — from DirectoryEntryDto.businessEmailDomain. Seeds an Email Intake Approved
   * Rule automatically once this connection is bound to a vendor. */
  targetBusinessEmailDomain?: string | null;
}

export interface RespondConnectionRequest {
  accept: boolean;
  message?: string | null;
  localVendorId?: string | null;
  localCustomerId?: string | null;
}

export interface EntitlementAnswerDto {
  enabled: boolean;
  message: string | null;
  checkedUtc: string;
}

/**
 * What iERP AI says this company may do. Probed, not assumed - the controller exists so the UI
 * can gate honestly rather than offering a button the central store will refuse.
 */
export interface TenantEntitlementsDto {
  companyDirectory: EntitlementAnswerDto;
  startHandshake: EntitlementAnswerDto;
  documentExchange: EntitlementAnswerDto;
  learningPublish: EntitlementAnswerDto;
}

export interface HandshakeDeliveryDto {
  id: string;
  deliveryId: string;
  deliveryType: string;
  connectionId: string | null;
  counterpartyCompanyName: string | null;
  bcSourceDocumentType: string | null;
  bcSourceDocumentNo: string | null;
  postedSalesInvoiceNo: string | null;
  sellerCompanyName: string | null;
  pdfFileName: string | null;
  pdfContentHash: string | null;
  status: string;
  statusMessage: string | null;
  attemptCount: number;
  acknowledged: boolean;
  ackedUtc: string | null;
  gatewayDocumentId: string | null;
  documentStatus: string | null;
  qboEntityId: string | null;
  qboDocNumber: string | null;
  receivedUtc: string;
  processedUtc: string | null;
}

export interface HandshakePullResponse {
  success: boolean;
  pulled: number;
  storedInvoices: number;
  deferred: number;
  replays: number;
  controlMessages: number;
  terminalRejected: number;
  message: string | null;
  platformApplied?: number;
  paymentNotices?: number;
}

export interface HandshakeExchangeStateDto {
  deliveryMethodConfigured: boolean;
  deliveryMethodConfiguredUtc: string | null;
  lastPullUtc: string | null;
  lastPullMessage: string | null;
}

export interface PartnerOutboundDeliveryDto {
  id: string;
  connectionId: string;
  counterpartyCompanyName: string | null;
  qboInvoiceId: string;
  qboDocNumber: string | null;
  qboCustomerId: string | null;
  currencyCode: string | null;
  amountIncludingTax: number | null;
  status: string;
  statusMessage: string | null;
  attemptCount: number;
  deliveryId: string | null;
  deliveryMethod: string | null;
  pdfFileName: string | null;
  pdfSizeBytes: number;
  payloadHash: string | null;
  acceptedUtc: string | null;
  createdByEmail: string | null;
  createdUtc: string;
  lastAttemptUtc: string | null;
}

export interface SendableInvoiceDto {
  qboInvoiceId: string;
  docNumber: string | null;
  txnDate: string | null;
  dueDate: string | null;
  currencyCode: string | null;
  totalAmt: number | null;
  balance: number | null;
  sentStatus: string | null;
  acceptedUtc: string | null;
}

// ---------------------------------------------------------------------------
// Platform parity (mirrors Contracts/PlatformContracts.cs)
// ---------------------------------------------------------------------------

/** This company's standing with the iERP platform: assignment, credential, lane, health. */
export interface PlatformStateDto {
  homeEnvironment: string | null;
  assignedEnvironment: string | null;
  assignedBaseUrl: string | null;
  effectiveBaseUrl: string;
  assignmentVersion: number;
  assignmentAcknowledgedVersion: number;
  temporaryOverride: boolean;
  assignmentReason: string | null;
  assignmentExpiresUtc: string | null;
  assignmentRefreshedUtc: string | null;
  assignmentMessage: string | null;
  configDataVersion: number;
  configAppliedUtc: string | null;
  serverRegistrationStatus: string | null;
  serverValidated: boolean;
  serverRequestedDeliveryMethod: string | null;
  clientStatus: string | null;
  billingConnectionId: string | null;
  billingConnectionStatus: string | null;
  catalogChangedUtc: string | null;
  lastCredentialRepairUtc: string | null;
  lastCredentialRepairMessage: string | null;
  healthCheckedUtc: string | null;
  health: unknown | null;
  deliveryLaneConfigured: boolean;
  lastPullUtc: string | null;
  lastPullMessage: string | null;
  currentAccessKeyId: string | null;
}

export interface PlatformDeliveryDto {
  id: string;
  deliveryId: string;
  deliveryType: string;
  dataVersion: number | null;
  sourceModifiedUtc: string | null;
  payloadHash: string | null;
  hashVerified: boolean | null;
  status: string;
  message: string | null;
  acknowledged: boolean;
  ackedUtc: string | null;
  receivedUtc: string;
}

/** One licence purchase and what became of it. */
export interface LicensePurchaseDto {
  id: string;
  licensePurchaseId: string;
  status: string | null;
  paymentStatus: string | null;
  paymentMethod: string | null;
  packageCode: string | null;
  basePackageCode: string | null;
  billingPeriod: string | null;
  currencyCode: string | null;
  lineAmount: number | null;
  amountToPay: number | null;
  amountIncludingTax: number | null;
  salesOrderNo: string | null;
  salesInvoiceNo: string | null;
  postedSalesInvoiceNo: string | null;
  stripePaymentUrl: string | null;
  paidUtc: string | null;
  lastMessage: string | null;
  source: string;
  createdByEmail: string | null;
  createdUtc: string;
  updatedUtc: string | null;
}

export interface PartnerPaymentNotificationDto {
  id: string;
  direction: string;
  notificationId: string;
  connectionId: string | null;
  counterpartyCompanyName: string | null;
  paymentStatus: string | null;
  paymentType: string | null;
  postingDate: string | null;
  documentNo: string | null;
  externalDocumentNo: string | null;
  paymentReference: string | null;
  currencyCode: string | null;
  amount: number;
  sourcePostedSalesInvoiceNo: string | null;
  matchedOutboundDeliveryId: string | null;
  gatewayDocumentId: string | null;
  qboBillPaymentId: string | null;
  status: string;
  statusMessage: string | null;
  acknowledged: boolean;
  publishedUtc: string | null;
  createdUtc: string;
}

export interface PartnerPaymentScanResponse {
  success: boolean;
  candidates: number;
  published: number;
  failed: number;
  message: string | null;
  /** Of the published notices, how many came from durable retry rather than a fresh QuickBooks read. */
  retried: number;
}

/**
 * 409 from POST /api/partners/payments/scan means the automatic pass for this company is already
 * running, so the forced pass did not start. Nothing was skipped.
 */
export const PARTNER_PAYMENT_PASS_BUSY_STATUS = 409;

// ---------------------------------------------------------------------------
// Setup Assistant
// ---------------------------------------------------------------------------

export interface AssistantModuleDto {
  key: string;
  appId: string;
  version: string;
  name: string;
  supported: boolean;
}

export interface AssistantCheckDto {
  id: string;
  module: string;
  title: string;
  status: string;
  detail: string | null;
  action: string | null;
  source: string | null;
  sequence: number;
  pending: boolean;
}

export interface AssistantActionDto {
  id: string;
  caption: string;
  route: string;
}

export interface AssistantFactDto {
  id: string;
  module: string;
  title: string;
  text: string;
  action: string | null;
  source: string;
  visibility: string;
}

export interface AssistantContextDto {
  modules: AssistantModuleDto[];
  checks: AssistantCheckDto[];
  actions: AssistantActionDto[];
  canAskAi: boolean;
  canAskAiReason: string | null;
  registered: boolean;
  knowledgeRevision: string;
  consent: string;
}

export interface AssistantAskRequest {
  question: string;
  previousFactIds: string[] | null;
}

export interface AssistantReplyDto {
  requestId: string;
  kind: string;
  mode: string;
  message: string;
  facts: AssistantFactDto[];
  checks: AssistantCheckDto[];
  actions: AssistantActionDto[];
  failure: string | null;
}

// ---------------------------------------------------------------------------
// AI Learning — historical evidence, attachments, publication, diagnostics
// (mirrors Contracts/EvidenceContracts.cs and Services/Learning/LearningDiagnosticsService.cs)
// ---------------------------------------------------------------------------

export interface EvidencePullApiRequest {
  entityType?: QboEntityType | null;
  maxRecords?: number | null;
}

export interface EvidencePullApiResponse {
  success: boolean;
  entityType: QboEntityType;
  read: number;
  created: number;
  updated: number;
  unchanged: number;
  /** True when the pass ended because the source ran out of rows, rather than hitting its page cap. */
  complete: boolean;
  /** True once the initial full enumeration of this entity type has ever completed. */
  backfillComplete: boolean;
  nextStartPosition: number;
  message: string | null;
  errorCode: string | null;
  /** Rows marked deleted by the authoritative full-scan seen-reconciliation. */
  missingMarked: number;
}

export interface EvidenceDeletionApiResponse {
  success: boolean;
  markedDeleted: number;
  changedSinceUtc: string | null;
  message: string | null;
  errorCode: string | null;
  /** True when at least one entity type's deletion checkpoint predates the CDC window. */
  fullRescanRequired: boolean;
  fullRescanRequiredFor: QboEntityType[];
}

export interface AttachmentEvidenceDto {
  id: string;
  attachableExternalId: string;
  entityType: QboEntityType | null;
  externalTransactionId: string | null;
  fileName: string | null;
  contentType: string | null;
  sizeBytes: number | null;
  contentHash: string | null;
  retrieved: boolean;
  retrievedUtc: string | null;
  deduplicated: boolean;
  status: string;
  retrievalMessage: string | null;
}

export interface AttachmentDiscoveryApiResponse {
  success: boolean;
  read: number;
  created: number;
  updated: number;
  complete: boolean;
  message: string | null;
  errorCode: string | null;
}

export interface AttachmentRetrievalApiResponse {
  success: boolean;
  attempted: number;
  retrieved: number;
  deduplicated: number;
  skipped: number;
  failed: number;
  message: string | null;
  errorCode: string | null;
}

/** Current publication (consent) state for this company's connector. */
export interface PublicationStateDto {
  consentGranted: boolean;
  consentGrantedByEmail: string | null;
  consentGrantedUtc: string | null;
  historyStartDate: string | null;
  includeAttachments: boolean;
  lastBatchId: string | null;
  lastPublishedUtc: string | null;
  publishedDocumentCount: number;
  lastPublishMessage: string | null;
}

export interface PublicationConsentApiRequest {
  granted: boolean;
  historyStartDate: string | null;
  includeAttachments: boolean;
}

export interface PublishApiResponse {
  success: boolean;
  documentsPublished: number;
  batchesSent: number;
  lastBatchId: string | null;
  message: string | null;
  errorCode: string | null;
}

/** Per entity type: how much evidence is held locally and how fresh it is. */
export interface EvidenceTypeDiagnostics {
  entityType: QboEntityType;
  role: string;
  transactionCount: number;
  deletedCount: number;
  lineCount: number;
  unpublishedCount: number;
  backfillComplete: boolean;
  backfillNextStartPosition: number;
  lastChangesSyncUtc: string | null;
  lastDeletionSyncUtc: string | null;
  lastMessage: string | null;
}

export interface AttachmentDiagnostics {
  discovered: number;
  retrieved: number;
  deduplicated: number;
  pendingRetrieval: number;
  deletedAtSource: number;
}

export interface GlVerificationDiagnostics {
  lastRunUtc: string | null;
  lastOutcome: string | null;
  lastVariance: number | null;
  lastMessage: string | null;
  discrepancyRuns: number;
}

export interface PublicationDiagnostics {
  consentGranted: boolean;
  consentGrantedByEmail: string | null;
  consentGrantedUtc: string | null;
  includeAttachments: boolean;
  lastPublishedUtc: string | null;
  publishedDocumentCount: number;
  unpublishedEligibleCount: number;
  lastMessage: string | null;
}

export interface MappingDiagnostics {
  groupAccounts: number;
  approvedMappings: number;
  pendingSuggestions: number;
  rejectedMappings: number;
}

export interface LearningEvidenceAvailability {
  localEvidenceAvailable: boolean;
  localPostingLineCount: number;
  groupLearningEnabled: boolean;
  groupPeerCount: number;
  peersWithEvidence: number;
}

// ---------------------------------------------------------------------------
// AI Learning — PER VENDOR consent and publish (mirrors Contracts/EvidenceContracts.cs's vendor
// records). AI Learning is enabled and published one vendor at a time, never company-wide, and a
// vendor's history may not be sent until that vendor's invoice layout has been taught — matching
// the Business Central MultiEntity ToolKit's own per-vendor "AI Learning Enabled" design.
// ---------------------------------------------------------------------------

/** One row of the vendor learning list. */
export interface VendorLearningSummaryDto {
  vendorExternalId: string;
  vendorName: string | null;
  enabledForLearning: boolean;
  /** True once this vendor's invoice layout has been taught — derived live from Vendor Intake
   * approval, never a cached flag that could drift from it. */
  layoutLearned: boolean;
  layoutLearnedUtc: string | null;
  unpublishedCount: number;
  lastPublishedUtc: string | null;
  publishedDocumentCount: number;
}

/** The full per-vendor consent/scope state, for that vendor's own learning setup drawer. */
export interface VendorLearningDetailDto {
  vendorExternalId: string;
  vendorName: string | null;
  enabledForLearning: boolean;
  layoutLearned: boolean;
  layoutLearnedUtc: string | null;
  layoutLearnedFromDocumentId: string | null;
  sendHistoricalLearning: boolean;
  historyStartDate: string | null;
  includeBills: boolean;
  includePurchases: boolean;
  includeVendorCredits: boolean;
  includeAttachments: boolean;
  maxAttachmentSamples: number;
  consentGrantedByEmail: string | null;
  consentGrantedUtc: string | null;
  lastBatchId: string | null;
  lastPublishedUtc: string | null;
  publishedDocumentCount: number;
  lastPublishMessage: string | null;
  lastPublishSucceeded: boolean;
  unpublishedCount: number;
}

export interface VendorLearningConsentApiRequest {
  enabledForLearning: boolean;
  sendHistoricalLearning: boolean;
  historyStartDate: string | null;
  includeBills: boolean;
  includePurchases: boolean;
  includeVendorCredits: boolean;
  includeAttachments: boolean;
  maxAttachmentSamples: number;
}

export interface VendorPublishApiResponse {
  success: boolean;
  documentsPublished: number;
  batchesSent: number;
  attachmentsPublished: number;
  lastBatchId: string | null;
  message: string | null;
  errorCode: string | null;
}

/** The one-call status read the AI Learning screen is built around. */
export interface LearningDiagnostics {
  connectorInstanceId: string;
  externalCompanyId: string;
  externalCompanyName: string | null;
  connectorStatus: string;
  lastEvidenceSyncUtc: string | null;
  accountCount: number;
  activeAccountCount: number;
  eligibleAccountCount: number;
  coaSnapshotVersion: number | null;
  coaSnapshotState: string | null;
  evidence: EvidenceTypeDiagnostics[];
  attachments: AttachmentDiagnostics;
  glVerification: GlVerificationDiagnostics;
  publication: PublicationDiagnostics;
  mappings: MappingDiagnostics;
  availability: LearningEvidenceAvailability;
}

// ---------------------------------------------------------------------------
// Email intake (forwarding tier)
// ---------------------------------------------------------------------------

/** Mirrors the bot's own vocabulary verbatim — NotConfigured, ReadyForForwarding, Disabled, Error. */
export type EmailIntakeStatus =
  | 'NotConfigured'
  | 'ReadyForForwarding'
  | 'ConsentRequired'
  | 'Connected'
  | 'Disabled'
  | 'Error'
  | string;

export type EmailIntakeTier = 'EmailIntake' | 'ConnectedMailbox' | string;

export interface EmailIntakeStatusDto {
  configured: boolean;
  enabled: boolean;
  /** 'EmailIntake' (Tier 1, forwarding) or 'ConnectedMailbox' (Tier 2, OAuth). */
  intakeTier: EmailIntakeTier;
  status: EmailIntakeStatus;
  inboundEmailAddress: string | null;
  /** Tier 2 only: the tenant's own mailbox address, once OAuth has completed. */
  connectedMailboxAddress: string | null;
  lastConnectedUtc: string | null;
  /** Tier 2 only, status ConsentRequired: open this to complete Microsoft's consent. Regenerated
   * on every status read — never assume yesterday's value is still valid. */
  consentUrl: string | null;
  requiresAdminConsent: boolean;
  lastErrorMessage: string | null;
  lastImportMessage: string | null;
  lastMonitorUtc: string | null;
  lastImportUtc: string | null;
  approvedRuleCount: number;
  /** Whether this tenant's package may use each tier, and the bot's own message about it — only
   * populated on a call that actually reached the bot. */
  tier1Allowed: boolean;
  tier2Allowed: boolean;
  entitlementMessage: string | null;
  /** Where the bot forwards a message from a sender at an approved domain whose exact address
   * isn't approved yet. Blank means nothing gets forwarded — the pending-senders list still works. */
  adminNotificationEmail: string | null;
  /** Senders waiting in the pending-senders queue for this connection. */
  pendingSenderCount: number;
  /** When true, an emailed invoice is never posted without a person looking at it — a sync rule
   * that says AutoPost is downgraded to review for this channel. */
  requireReviewBeforeApply: boolean;
  minimumConfidencePercent: number;
  /** When false, an email with no supported attachment is still classified against its own body
   * text instead of being ignored — for an invoice typed directly into the email. */
  processAttachmentsOnly: boolean;
  /** How hard the bill-to company check bites. */
  companyMatchMode: CompanyMatchMode;
  /** Registered alternates. Worth showing: relying on these is normal at first and permanent only
   * if nobody notices how many there are. */
  companyAliasCount: number;
  /** False when iERP AI holds none of this company's own name and address, which means invoices
   * are being taken in without any check that they are addressed to THIS company. Worth saying out
   * loud: from every other signal on this page, that state looks exactly like healthy operation. */
  companyIdentityKnown: boolean;
  /** The company identity the bill-to check runs against, on one line. */
  companyIdentity: string | null;
  /** Invoices held because the document says they belong to a different company. */
  heldForCompanyReviewCount: number;
}

export interface ActivateEmailIntakeRequest {
  apMailboxAddress: string | null;
  requireReviewBeforeApply: boolean;
  minimumConfidencePercent: number;
  intakeTier: EmailIntakeTier;
  adminNotificationEmail: string | null;
  processAttachmentsOnly?: boolean;
}

/** A sender at an approved domain whose exact address is not — held instead of guessed at, since
 * the domain can front more than one vendor. */
export interface EmailIntakePendingSenderDto {
  pendingSenderId: string;
  fromAddress: string;
  domain: string;
  subject: string;
  receivedUtc: string | null;
  status: string;
  forwarded: boolean;
  /** Vendors already registered for this sender's domain — offered as quick picks for Add. */
  candidateVendors: { qboVendorId: string; vendorName: string }[];
}

export interface DecideEmailIntakePendingSenderRequest {
  decision: 'Ignore' | 'Add';
  qboVendorId: string | null;
  vendorName: string | null;
}

export interface DisableEmailIntakeRequest {
  reason: string | null;
}

/** The tenant's own registered forwarding/AP mailbox address — the absolute gate on whether the
 * bot reads a Tier 1 message at all, checked before any vendor rule. */
/**
 * An invoice the extraction found is addressed to a DIFFERENT company, held for a person.
 *
 * The address an email arrived on is not evidence of whose invoice it is: a vendor with one AP
 * contact for a family of companies sends everything to whichever address it was first given. What
 * the document itself says is the evidence, and this is what it said.
 */
export interface EmailIntakeHeldInvoiceDto {
  documentId: string;
  fileName: string;
  fromAddress: string | null;
  vendorName: string | null;
  invoiceNumber: string | null;
  invoiceTotal: number | null;
  currencyCode: string | null;
  receivedUtc: string | null;
  /** Who the invoice says it is addressed to. */
  billToName: string | null;
  billToAddress: string | null;
  /** Which of this company's own details the billing party disagreed with. */
  warning: string | null;
  /** A sibling company in the same group whose name matches. A suggestion only. */
  suggestedCompany: string | null;
  /** This company, so the two can be compared side by side. */
  thisCompany: string | null;
}

export type EmailIntakeHeldInvoiceDecision = 'Accept' | 'Reject';

/**
 * How hard the bill-to company check bites.
 * - Enforce  — an invoice addressed to another company is held for a person.
 * - WarnOnly — the mismatch is recorded and shown, but the invoice is not held.
 * - Off      — no verdict at all; the billing party is still recorded.
 */
export type CompanyMatchMode = 'Enforce' | 'WarnOnly' | 'Off';

export interface SetCompanyMatchModeRequest {
  mode: CompanyMatchMode;
}

/** Billing details that should also be accepted as this company — typically an address a vendor
 * has not got round to updating. Every field that is filled in has to match. */
export interface EmailIntakeCompanyAliasDto {
  id: string;
  tenantId: string;
  name: string | null;
  addressContains: string | null;
  note: string | null;
  createdUtc: string;
  createdByEmail: string | null;
}

export interface AddCompanyAliasRequest {
  name: string | null;
  addressContains: string | null;
  note: string | null;
}

export interface ResolveHeldInvoiceRequest {
  decision: EmailIntakeHeldInvoiceDecision;
}

export interface EmailIntakeForwarderDto {
  id: string;
  tenantId: string;
  emailAddress: string;
  label: string | null;
  createdUtc: string;
  createdByEmail: string | null;
}

export interface UpsertEmailIntakeForwarderRequest {
  emailAddress: string;
  label: string | null;
}

export interface EmailIntakeApprovedRuleDto {
  id: string;
  tenantId: string;
  matchType: 'Domain' | 'ExactAddress' | string;
  matchValue: string;
  qboVendorId: string;
  vendorName: string;
  createdUtc: string;
  createdByEmail: string | null;
}

export interface UpsertEmailIntakeRuleRequest {
  matchType: 'Domain' | 'ExactAddress';
  matchValue: string;
  qboVendorId: string;
  vendorName: string;
}

/**
 * RunNow answers one of two shapes: `{ success: false, message }` when intake is not enabled (or
 * has no registration to run against), or the full import outcome once a monitor pass actually
 * ran. Both are covered here rather than as a union, since the caller only ever reads success/
 * message plus the counts that are present only on the second shape.
 */
export interface EmailIntakeRunNowResult {
  success: boolean;
  message: string | null;
  itemsSeen?: number;
  imported?: number;
  acknowledged?: number;
}

// ---------------------------------------------------------------------------
// Payables Agent
// ---------------------------------------------------------------------------

export interface PayablesAgentSetupDto {
  id: string;
  tenantId: string;
  enabled: boolean;
  learningEnabled: boolean;
  /** Share payment behaviour with iERP AI and use the vendor payment profiles it returns. */
  botLearningEnabled: boolean;
  lastBotSyncUtc: string | null;
  lastBotSyncMessage: string | null;
  botProfilesAppliedUtc: string | null;
  schedulingEnabled: boolean;
  executionEnabled: boolean;
  /** The stage a vendor starts at the first time the agent sees an open bill for it. A vendor
   * already on the ladder keeps its own stage regardless of this default. */
  defaultNewVendorStage: VendorAgentStage;
  requireUserConfirmation: boolean;
  defaultBankAccountId: string | null;
  defaultBankAccountName: string | null;
  defaultPayType: string;
  minLeadDays: number;
  capAtDueDate: boolean;
  followHistoryPastDue: boolean;
  snapToRunDay: boolean;
  runOnMondays: boolean;
  runOnTuesdays: boolean;
  runOnWednesdays: boolean;
  runOnThursdays: boolean;
  runOnFridays: boolean;
  runOnSaturdays: boolean;
  runOnSundays: boolean;
  fallbackDays: number;
  rollOffWeekends: boolean;
  learningWindowMonths: number;
  minSamples: number;
  profileRefreshHours: number;
  autoPostMaxAmount: number;
  lastRunStartedUtc: string | null;
  lastRunCompletedUtc: string | null;
  lastRunMessage: string | null;
  lastErrorMessage: string | null;
  profilesComputedUtc: string | null;
  lastResolvedPaymentUtc: string | null;
}

export interface UpdatePayablesAgentSetupRequest {
  enabled: boolean;
  learningEnabled: boolean;
  /** Share payment behaviour with iERP AI. Omitting this from a PUT turns sharing OFF, so every
   * caller must send the current value rather than only the fields it means to change. */
  botLearningEnabled: boolean;
  schedulingEnabled: boolean;
  executionEnabled: boolean;
  defaultNewVendorStage: VendorAgentStage;
  requireUserConfirmation: boolean;
  defaultBankAccountId: string | null;
  defaultBankAccountName: string | null;
  defaultPayType: string;
  minLeadDays: number;
  capAtDueDate: boolean;
  followHistoryPastDue: boolean;
  snapToRunDay: boolean;
  runOnMondays: boolean;
  runOnTuesdays: boolean;
  runOnWednesdays: boolean;
  runOnThursdays: boolean;
  runOnFridays: boolean;
  runOnSaturdays: boolean;
  runOnSundays: boolean;
  fallbackDays: number;
  rollOffWeekends: boolean;
  learningWindowMonths: number;
  minSamples: number;
  profileRefreshHours: number;
  autoPostMaxAmount: number;
}

export interface ScheduledPaymentDto {
  id: string;
  tenantId: string;
  connectorInstanceId: string | null;
  qboVendorId: string;
  vendorName: string | null;
  billId: string;
  billDocNumber: string | null;
  billTxnDateUtc: string;
  dueDateUtc: string | null;
  discountDateUtc: string | null;
  originalAmount: number;
  remainingAmount: number;
  currencyCode: string | null;
  suggestedDateUtc: string;
  scheduledDateUtc: string;
  scheduleSource: PaymentScheduleSource;
  confidence: PayProfileConfidence;
  explanation: string | null;
  status: ScheduledPaymentStatus;
  holdReason: string | null;
  billPaymentId: string | null;
  paidUtc: string | null;
  /** The idempotency key of the payment attempt in flight, if any. */
  paymentRequestId: string | null;
  paymentClaimedUtc: string | null;
  /** Why an attempt's outcome is not known. Present only alongside PaymentOutcomeUnknown. */
  paymentOutcomeMessage: string | null;
  correctionDays: number | null;
  correctionType: string | null;
  createdUtc: string;
  modifiedUtc: string;
  modifiedByEmail: string | null;
}

export interface VendorPaymentProfileDto {
  id: string;
  tenantId: string;
  qboVendorId: string;
  vendorName: string | null;
  hasProfile: boolean;
  sampleCount: number;
  medianDaysToPay: number;
  p25DaysToPay: number;
  p75DaysToPay: number;
  trimmedMeanDaysToPay: number;
  medianDaysVsDue: number;
  onTimeRatio: number;
  discountCaptureRatio: number;
  partialPaymentRatio: number;
  dominantWeekday: number;
  weekdayShare: number;
  confidence: PayProfileConfidence;
  biasDays: number;
  correctionCount: number;
  lastPaymentUtc: string | null;
  computedUtc: string;
  explanation: string | null;
}

export interface VendorPayAgentOverrideDto {
  id: string;
  tenantId: string;
  qboVendorId: string;
  vendorName: string | null;
  excludeFromAgent: boolean;
  /** Where this vendor is on the ladder right now. */
  stage: VendorAgentStage;
  stageAdvancedUtc: string | null;
  stageAdvancedByEmail: string | null;
  stageNote: string | null;
  overrideDays: number;
  createdUtc: string;
}

/** A vendor's ladder position joined with the evidence for its next rung, matching
 * PayablesAgentController.VendorStageDto's PascalCase members exactly (this endpoint returns the
 * two entities as-is rather than a flattened DTO). */
export interface VendorStageRow {
  override: VendorPayAgentOverrideDto;
  profile: VendorPaymentProfileDto | null;
}

export interface AdvanceVendorStageRequest {
  note: string | null;
}

export interface SetVendorStageRequest {
  stage: VendorAgentStage;
  note: string | null;
}

export interface SetVendorExclusionRequest {
  excludeFromAgent: boolean;
}

export interface SetVendorOverrideDaysRequest {
  overrideDays: number;
}

export interface PayablesAgentLogEntryDto {
  id: string;
  tenantId: string;
  area: string;
  status: string;
  message: string | null;
  errorMessage: string | null;
  runSource: string;
  scheduledPaymentId: string | null;
  qboVendorId: string | null;
  createdUtc: string;
}

export interface PayablesAgentRunNowResult {
  success: boolean;
  message?: string;
  samplesInserted: number;
  newlyScheduled: number;
  executed: number;
  readyForPayment: number;
  sentForApproval: number;
  held: number;
}
